import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';

import { EDefultCanje, INivelesPromo, IProductos, IPromosEcommerce } from '../../../private/shared/models/productos';
import { TransferDataService } from '../../../private/shared/services/transfer-data.service';
import { EAnalyticsPageViews, ESectionProperty } from '../../enums/analytics.enum';
import { EventInteraction, GTMInteractions } from '../../models/Constants';
import { AnalyticsService } from '../../services/analytics.service';
import { ModalsService } from '../../services/modals/modals.service';
import { OperationsService } from '../../services/operations/operations.service';

@Component({
  selector: 'app-promo-detail',
  templateUrl: './promo-detail.page.html',
  styleUrls: ['./promo-detail.page.scss'],
})
export class PromoDetailPage implements OnInit, OnDestroy {

  @Input() promo: IPromosEcommerce = {} as IPromosEcommerce;
  @Input() origin: string = '';

  public disabledEdit = true;
  public isIOS = false;
  public level = 0;
  public exchange = 0;
  public intialCanje = 0;
  public levelStairs = 0;
  public productosPromocion: IProductos[] = [];
  public cartPromoProducts: IProductos[] = [];
  public NivelesPromo: INivelesPromo[] = [];
  public promosCart: IPromosEcommerce = this.promo;
  public canjeEnabled = { isVisible: false, isActive: false, isCheck: false, maximum: 0 };

  private finishSubscriptions: Subject<boolean> = new Subject();

  constructor(
    private transferDataS: TransferDataService
    , private operationsS: OperationsService
    , private analyticsS: AnalyticsService
    , private modalsS: ModalsService
    , private modalCtrl: ModalController
  ) {
    this.isIOS = Capacitor.getPlatform() === 'ios';
  }

  async ngOnInit() {
    this.intialCanje = EDefultCanje[this.promo.TipoPromocion];
    await this.sendEventPromoDetail();
  }

  ngOnDestroy() {
    this.modalsS.modalDismiss();
    this.finishSubscriptions.next(true);
    this.finishSubscriptions.complete();
    this.NivelesPromo = [];
  }

  /**
   * Regresa a la pantalla anterior
   */
  public onBack = async (event: CustomEvent | MouseEvent) => this.onCancel(event);

  /** Cierra de detalle de la promo */
  public onCancel = (event: CustomEvent | MouseEvent) => {
    switch (this.promo.TipoPromocion) {
      case 'DINAMICO':
        this.analyticsS.sendEventAnalytics(GTMInteractions.cancelDynamicPromoEvent, EventInteraction);
        break;
      case 'ESCALERA':
        this.analyticsS.sendEventAnalytics(GTMInteractions.cancelStairPromoEvent, EventInteraction);
        break;
      case 'BONIFICACION':
        this.analyticsS.sendEventAnalytics(GTMInteractions.cancelDynamicPromoEvent, EventInteraction)
        break;
    }
    this.modalCtrl.dismiss(false);
  }

  public cartPromoProductsEvent = (event: IProductos[]) => {
    this.cartPromoProducts = event;
    this.canjeEnabled = this.visibleRedeem(this.promo);
  }

  /** Pasa por referencia el estado del carrito y nivel los productos seleccionados - ESCALERA */
  public productUse = (product: IProductos) => {
    this.cartPromoProducts = this.cartPromoProducts.map(p => product.id_producto === p.id_producto ? product : p);

    const selected = this.transferDataS.sumTotal(this.cartPromoProducts, 'productos');
    const minimum = this.promo.Niveles.filter(n => selected >= n.Minimo && selected <= n.Maximo);

    this.levelStairs = minimum.length > 0 ? minimum[0].Nivel : 0;
    this.cartPromoProducts = this.cartPromoProducts.map(p => (
      {
        ...p,
        Nivel: this.levelStairs,
        precio: minimum.length < 1 ? p.precio_original : p.precio_original * (1 - minimum[0].Descuento)
      }
    ));
  }

  /** Retorno del avance de cada nivel por referencia */
  public comeNivelProducts = (value: { Niveles: INivelesPromo[], cartPromoProducts: IProductos[] }) => {
    this.NivelesPromo = value.Niveles;
    this.cartPromoProducts = value.cartPromoProducts;
    this.canjeEnabled = this.visibleRedeem(this.promo);
  }

  /**
   * Si se muestra el componente de canje de promos,
   * este habilitado el canje
   * estatus del icono,
   * dependieno del tipo de promo
   */
  private visibleRedeem = (promo: IPromosEcommerce) => {
    let canjeEnabled = { isVisible: false, isActive: false, isCheck: false, maximum: 0 };
    const maximum = promo.MaximoPaquetes - (promo.PaquetesCanjeados - promo.UltimosCanjeados);
    const minimum = promo.Niveles.length > 0 ? promo.Niveles.find(n => n.Nivel === 1)?.Minimo || 0 : 0;
    const selected = this.transferDataS.sumTotal(this.cartPromoProducts, 'productos');

    switch (promo.TipoPromocion) {
      case 'DINAMICO':
        canjeEnabled = {
          isVisible: !this.validLevels(),
          isActive: this.cartPromoProducts.length > 0,
          isCheck: !this.NivelesPromo.some(n => n.Maximo > n.Minimo),
          maximum
        };
        break;
      case 'ESCALERA':
        canjeEnabled = {
          isVisible: false,
          isActive: selected >= minimum,
          isCheck: selected >= minimum && this.exchange > 0,
          maximum
        };
        break;
      case 'BONIFICACION':
        canjeEnabled = {
          isVisible: !this.validLevels(),
          isActive: this.cartPromoProducts.length > 0,
          isCheck: !this.NivelesPromo.some(n => n.Maximo > n.Minimo),
          maximum
        };
        break;
    }

    return canjeEnabled;
  }

  /** Si cumple con los niveles */
  public validLevels = () => this.NivelesPromo.some(n => n.Maximo > n.Minimo);

  /** Agrega los paquetes a canjear */
  public addExchange = (select: number) => this.exchange = select;

  /** Variables generales default al cancelar o agregar */
  public onAdd = (isPromoAdded: boolean = false) => {
    this.NivelesPromo = [];
    this.modalCtrl.dismiss(isPromoAdded);
  }

  /** Envia evento de analytics al entrar al detalle de las promociones */
  public sendEventPromoDetail = async (): Promise<void> => {
    const eventView = this.promo.TipoPromocion === 'DINAMICO' ? EAnalyticsPageViews.promoDynamicView : this.promo.TipoPromocion === 'ESCALERA' ? EAnalyticsPageViews.promoStairView : this.promo.TipoPromocion === 'BONIFICACION' ? EAnalyticsPageViews.promoBonificacionView : null;
    if (eventView !== null) {
      await this.analyticsS.sendEventGTM(eventView, { addEnvironmentProps: true, addPageViewProps: true });

      const productsPromo = this.promo.Productos.map(p => ({ ...p, productos: 1 }));
      const isHome = this.origin === '/main/Tienda'
      const listOriginName = isHome ? 'Home' : 'Promoción'
      const listType = isHome ? 'Carrusel' : 'Listado'

      this.analyticsS.clearEcommerceEvents();
      this.analyticsS.sendEventViewItemListGTM(productsPromo, 'promociones', listOriginName, listType, "Promociones", ESectionProperty.tienda, { addEnvironmentProps: true }, this.promo);

    }
  }
}
